import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import { createMarkerHashedKey } from 'libraries/mapLibrary';
import { PaidPlanSummaryCard, BasicPlanSummaryCard } from 'features/summaryCard';
import {
  callForPriceHomeSvgHtml,
  priceReducedSvgHtml,
  callForPriceHomeActiveSvgHtml,
  markerTipSvgHtml
} from '../constants/svgIcons';
import MarkerType from '../enums/markerType';
import { isPlanViewed } from '../services/markerSessionService';
import getClassesToApply from '../services/unitMarkerService';

/* Creates a new instance of a housing unit that will be plotted in the map as a marker. */
export default class PlanUnitMarker {
  constructor(housing) {
    const totalPlans = housing.TotalPlans ?? 1;
    this.geoId = housing.GeoId;
    this.detailText = housing.DetailText;
    this.type = MarkerType.PlanUnit.toString();
    this.id = housing.Id;
    this.communityId = housing.CommunityId;
    this.latitude = housing.Latitude;
    this.longitude = housing.Longitude;
    this.priceText = housing.PriceText;
    this.shortPrice = housing.ShortPrice;
    this.title = housing.Title;
    this.totalPlans = +totalPlans;
    this.previousPrice = housing.PreviousPrice;
    this.priceReducedTrackingLabel = housing.PriceReducedTrackingLabel;
    this.price = housing.Price;
    this.hasInteractiveSitePlan = housing.HasInteractiveSitePlan;
    this.sitePlanCenterLatitude = housing.SitePlanCenterLatitude;
    this.sitePlanCenterLongitude = housing.SitePlanCenterLongitude;
    this.isBasic = housing.IsBasic || housing.IsBl;
    this.isFeaturedListing = housing.IsFeaturedListing;
    this.isCallForPrice = housing.IsCallForPrice;
    this.isMasterPlannedCommunity = housing.IsMasterPlannedCommunity || false;
    this.isAdult = housing.IsAdult || false;
    this.isViewed = isPlanViewed(housing.Id);
    this.thumbnail = housing.Thumbnail;
    this.bedrooms = housing.Bedrooms;
    this.bathrooms = housing.Bathrooms;
    this.address = housing.Address;
    this.brandName = housing.BrandName;
    this.squareFeet = housing.SquareFeetRange;
    this.planTypeName = housing.PlanTypeName;
    this.city = housing.City;
    this.stateAbbreviation = housing.StateAbbreviation;
    this.zip = housing.Zip;
    this.garages = housing.Garages;
    this.name = housing.Name;
    this.communityName = housing.CommunityName;
    this.communityStatus = housing.CommunityStatus;
    this.averageRating = housing.AverageRating;
    this.hasSelfGuidedTour = housing.HasSelfGuidedTour;
    this.builderId = housing.BuilderId;

    if (this.isBasic) {
      this.zIndex = 1;
    } else if (this.isFeaturedListing) {
      this.zIndex = 5;
    } else {
      this.zIndex = 2;
    }
  }

  /**
   * getMarkerText: gets the text the marker is going to show once rendered.
   * @returns a text to show on the map once rendered.
   */
  getMarkerText() {
    return this.shortPrice;
  }

  /**
   * getMarkerTextNode: gets the html node to represent the marker text
   * @returns a string representing the html node to render a marker text
   */
  getMarkerTextNode() {
    if (this.shortPrice && !this.isCallForPrice) {
      return `<div data-marker-text class="nhs-c-marker__text" data-qa="marker_price">
                ${this.shortPrice}
              </div>`;
    }

    return '';
  }

  /**
   * @returns The name of the extend marker type.
   */
  getType() {
    return this.type;
  }

  /**
   * It returns a string that is the url to the detail page of the home plan
   * @returns The string `/homedetail/planid-${this.id}`
   */
  getDetailUrl() {
    return `/homedetail/planid-${this.id}`;
  }

  /**
   * This function returns a new GoogleAnalyticsEvent object with the event category, event action, and
   * event label set.
   * @returns A new instance of the GoogleAnalyticsEvent class.
   */
  getGoogleAnalyticsEventForClicks() {
    const customDimensions = {
      [GoogleAnalyticsCustomDimensions.PriceReduction().getName()]:
        this.priceReducedTrackingLabel,
      [GoogleAnalyticsCustomDimensions.PlanId().getName()]: this.id,
      [GoogleAnalyticsCustomDimensions.IsPaid().getName()]: !this.isBasic
    };
    const url = this.getDetailUrl();
    return new GoogleAnalyticsEvent(
      'Map-Pin-Clicks',
      'Homes: SRP-Map-Home-Clicks',
      url,
      customDimensions
    );
  }

  /**
   * This function returns a new meta data object with the required information based on the map pin data.
   * @returns A new instance of the meta data object.
   */
  getHousingLocationMetaData() {
    return {
      priceReducedTrackingLabel: this.priceReducedTrackingLabel,
      planId: this.id,
      isPaid: !this.isBasic,
      priceLow: this.price,
      hasInteractiveSitePlan: this.hasInteractiveSitePlan,
      communityId: this.communityId,
      id: this.id,
      thumbnail: this.thumbnail,
      bedrooms: this.bedrooms,
      bathrooms: this.bathrooms,
      address: this.address,
      brandName: this.brandName,
      squareFeet: this.squareFeet,
      planTypeName: this.planTypeName,
      city: this.city,
      stateAbbreviation: this.stateAbbreviation,
      zip: this.zip,
      garages: this.garages,
      name: this.name,
      communityName: this.communityName,
      priceText: this.priceText,
      sitePlanCenterLatitude: this.sitePlanCenterLatitude,
      sitePlanCenterLongitude: this.sitePlanCenterLongitude,
      communityStatus: this.communityStatus,
      url: this.getDetailUrl(),
      averageRating: this.averageRating,
      previousPrice: this.previousPrice,
      hasSelfGuidedTour: this.hasSelfGuidedTour,
      isFeaturedListing: this.isFeaturedListing,
      builderId: this.builderId
    };
  }

  /**
   * This function returns the HTML in charge of build the hover card of a given marker
   * @returns hover card HTML
   */
  getCard() {
    return this.isBasic ? new BasicPlanSummaryCard() : new PaidPlanSummaryCard();
  }

  /**
   * @returns {string} The complete HTML that represents the housing unit marker as an icon and a card with details.
   */
  getIcon() {
    const htmlPinString = this.getHtmlPinElement();
    const card = this.getCard();
    card.setInformation({
      id: this.id,
      name: this.name,
      communityName: this.communityName,
      communityId: this.communityId,
      url: this.getDetailUrl(),
      price: this.priceText,
      previousPrice: this.previousPrice,
      isFeaturedListing: this.isFeaturedListing,
      isLimited: !!this.isCallForPrice,
      thumbnail: this.thumbnail,
      latitude: this.latitude,
      longitude: this.longitude,
      zip: this.zip,
      city: this.city,
      stateAbbreviation: this.stateAbbreviation,
      builderId: this.builderId,
      brandName: this.brandName,
      builderRating: this.averageRating,
      bedrooms: this.bedrooms,
      bathrooms: this.bathrooms,
      garages: this.garages,
      squareFeet: this.squareFeet,
      sitePlanCenterLatitude: this.sitePlanCenterLatitude,
      sitePlanCenterLongitude: this.sitePlanCenterLongitude,
      marketingStatus: this.communityStatus
    });
    
    return `
      <div data-marker-content class="nhs-c-advance-marker nhs-c-tooltip-hide">
        ${htmlPinString}
        ${card.getHtml()}
      </div>
    `;
  }

  /**
   * getMarkerIcon: gets the html in charge of representing the marker icon given some important attribute of it,
   * like being a price reduction marker, call for price marker, among others.
   * @returns a html string representing the marker icon.
   */
  getMarkerIcon() {
    if (this.isCallForPrice) {
      return `
      <div class="nhs-c-marker__icon-no-price">
        <div class="nhs-c-marker__icon--default">${callForPriceHomeSvgHtml}</div>
        <div class="nhs-c-marker__icon--hover">${callForPriceHomeActiveSvgHtml}</div>
      </div>`;
    }

    if (this.previousPrice) {
      return `<div class="nhs-c-marker__icon">${priceReducedSvgHtml}</div>`;
    }

    return '';
  }

  /**
   * getDescription: returns a description that will help screen readers
   * provide a better explanation about the marker itself.
   * @returns a description for the marker.
   */
  getDescription() {
    if (this.shortPrice) {
      return `Plan from ${this.shortPrice}`;
    }

    return this.title;
  }

  /**
   * getMarkerStyles function is in charge of returning an array with the classes to use for the map marker.
   * @returns an array representing all the classes we should apply to the map marker in terms of its characteristics.
   */
  getMarkerStyles() {
    const { base } = getClassesToApply({
      isMasterPlannedCommunity: this.isMasterPlannedCommunity,
      isSpotlight: this.isFeaturedListing,
      isAdult: this.isAdult,
      isViewed: this.isViewed
    });

    const classesToApply = [...base].filter(Boolean);
    return classesToApply;
  }

  /**
   * Creates an HTML string representing a pin marker of the plan unit.
   * For basic markers, returns a minimal div without arrows or inner content.
   * For all others, includes marker icon, text, and arrow elements.
   * @returns {string} The HTML string for the marker pin.
   */
  getHtmlPinElement() {
    const markerStyles = this.getMarkerStyles().join(' ');
    const dataAttributes = [
      `data-marker-icon="true"`,
      `class="${markerStyles}"`,
      `data-id="${this.id}"`,
      `data-type="${this.getType()}"`,
      `data-geographic-key="${createMarkerHashedKey(
        this.latitude,
        this.longitude
      )}"`,
      `data-price-reduced-tracking-label="${this.priceReducedTrackingLabel}"`,
      `data-is-spotlight="${this.isFeaturedListing}"`,
      `data-is-no-price="${this.isCallForPrice}"`,
      `data-viewed="${this.isViewed}"`,
      `data-is-price-reduction="${!!this.previousPrice}"`,
      `data-qa="marker_container"`,
      this.isBasic ? `data-is-basic="true"` : `data-is-paid="true"`
    ].join(' ');

    if (!this.shortPrice || this.isCallForPrice) {
      return `<div ${dataAttributes}></div>`;
    }

    return `
      <div ${dataAttributes}>        
        <div class="nhs-c-marker">
          <div class="nhs-c-marker__box">
            ${this.getMarkerTextNode()}
            <div class="nhs-c-marker__arrow--tip">${markerTipSvgHtml}</div>
          </div>
        </div>
      </div>
    `;
  }
}
